import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useGetPrettyNumbersData } from '@/api';
import { OperatorType } from '@/api/enums/Operators.ts';
import { GetPrettyNumbersParams } from '@/api/types';
import { CircleIcon } from '@/components/icons/category';
import { ActiveFilter } from '@/components/PrettyNumbers/ActiveFiltersList/ActiveFilter/ActiveFilter.tsx';
import { ActiveFilterContentWithTotal } from '@/components/PrettyNumbers/ActiveFiltersList/ActiveFilter/ActiveFilterWithTotal/ActiveFilterWithTotal.tsx';
import { DisabledFilter } from '@/components/PrettyNumbers/ActiveFiltersList/ActiveFilter/DisabledFilter/DisabledFilter.tsx';
import { appRoutes } from '@/routes/app-routes.ts';
import { useFilterStore } from '@/store';
import { SearchFilters } from '@/types';
import { filterCategories, getEnumKeyByValue } from '@/utils';

import cn from './styles.module.sass';

export const ActiveFiltersList = () => {
    const {
        searchParamsStr,
        searchParams,
        clearCategories,
        clearOperators,
        clearTariffsVal,
        clearRegionsVal,
        clearPrefix,
        clearPriceRange,
        clearDeviceTypes,
        setIsFiltersOpen,
    } = useFilterStore();

    const { pathname } = useLocation();

    const { data } = useGetPrettyNumbersData();

    const handleClearFilter = (
        filterName: keyof (GetPrettyNumbersParams['search'] & {
            priceRange: string;
            device: number[];
        })
    ) => {
        switch (filterName) {
            case 'prefix':
                return clearPrefix;
            case 'categories':
                return clearCategories;

            case 'operators':
                return clearOperators;

            case 'tariffs':
                return clearTariffsVal;
            case 'regions':
                return clearRegionsVal;
            case 'priceRange':
                return clearPriceRange;
            case 'device':
                return clearDeviceTypes;
        }
    };

    const activeFilters = useMemo(() => {
        const { priceMin, priceMax, categories } = searchParams;
        const searchParamsWithPriceRange = {
            ...searchParams,
            priceRange: priceMin || priceMax ? `${priceMin || 0} - ${priceMax || '∞'} ₽` : '',
            categories: filterCategories(data?.categories, categories || []),
        };
        return Object.entries(searchParamsWithPriceRange)
            .filter(
                ([key]) =>
                    key !== 'prefix' &&
                    key !== 'msisdnMask' &&
                    key !== 'isFree' &&
                    key !== 'priceMin' &&
                    key !== 'priceMax' &&
                    (pathname.includes(appRoutes.goodTariffs)
                        ? key !== 'categories' && key !== 'regions' && key !== 'tariffs'
                        : true)
            )
            .map(([key, value]) => {
                if (Array.isArray(value)) {
                    if (key !== 'categories' && key !== 'operators') {
                        return {
                            total: value.length,
                            name: key,
                            displayName: SearchFilters[key as keyof typeof SearchFilters],
                            active: value.length > 0,
                        };
                    }
                    return {
                        value:
                            key === 'categories'
                                ? value
                                : value.map(operatorId =>
                                      getEnumKeyByValue(OperatorType, operatorId as number)
                                  ),
                        name: key,
                        displayName: SearchFilters[key as keyof typeof SearchFilters],
                        active: value.length > 0,
                    };
                }
                return {
                    value,
                    name: key,
                    active: !!value,
                    displayName: SearchFilters[key as keyof typeof SearchFilters],
                };
            })
            .sort(a => (a.active ? -1 : 1));
    }, [searchParamsStr]);

    const handleClick = () => {
        setIsFiltersOpen(true);
    };

    return (
        <Swiper className={cn.swiper} slidesPerView="auto" spaceBetween={4}>
            {activeFilters.map(filter => {
                if (!('value' in filter)) {
                    return (
                        <SwiperSlide key={filter.name} className={cn.swiperSlide}>
                            <ActiveFilter
                                onClick={handleClick}
                                disabled={!filter.active}
                                onClear={handleClearFilter(
                                    filter.name as keyof GetPrettyNumbersParams['search']
                                )}
                            >
                                {filter.active ? (
                                    <ActiveFilterContentWithTotal
                                        totalSelected={filter.total}
                                        filterName={filter.displayName}
                                    />
                                ) : (
                                    <DisabledFilter filterName={filter.displayName} />
                                )}
                            </ActiveFilter>
                        </SwiperSlide>
                    );
                }

                return (
                    <SwiperSlide key={filter.name} className={cn.swiperSlide}>
                        {filter.active ? (
                            <ActiveFilter
                                onClick={handleClick}
                                onClear={handleClearFilter(
                                    filter.name as keyof GetPrettyNumbersParams['search']
                                )}
                            >
                                {Array.isArray(filter.value) ? (
                                    <div className={cn.itemsList}>
                                        {filter.value.map(item =>
                                            filter.name === 'categories' &&
                                            typeof item === 'object' ? (
                                                <span key={item.id} style={{ color: item.color }}>
                                                    {CircleIcon}
                                                </span>
                                            ) : (
                                                <div
                                                    key={item as string}
                                                    className={cn.iconWrapper}
                                                >
                                                    <img
                                                        src={`/images/icons/operators/${item}.svg`}
                                                        alt=""
                                                    />
                                                </div>
                                            )
                                        )}
                                    </div>
                                ) : (
                                    <span>{filter.value}</span>
                                )}
                            </ActiveFilter>
                        ) : (
                            <ActiveFilter onClick={handleClick} disabled={!filter.active}>
                                <DisabledFilter filterName={filter.displayName} />
                            </ActiveFilter>
                        )}
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );
};
